import { type QueryClient, useQueryClient } from "@tanstack/vue-query";
import { onBeforeUnmount, onMounted } from "vue";
import { getAuthToken } from "@/shared/http/client";
import { readEventStream } from "@/shared/http/sse";
import { notificationKeys } from "./use-notifications";

/**
 * O sino a ouvir, na aba que não fez nada.
 *
 * ── POR QUE ISTO E NÃO UM RELÓGIO ─────────────────────────────────────────
 *
 * O sino perdeu o `refetchInterval` a 27/08 (zero polling, decisão do dono) e
 * ficou a depender do foco da janela e das próprias mutações. Uma notificação
 * que uma automação escreve enquanto o painel está aberto — o aviso que o manual
 * promete "chega no sino sem F5" — não chegava: quem estava a olhar
 * para o painel não muda de janela, e por isso nunca o refocava. Este fio é o
 * passo que o comentário de então deixou anotado.
 *
 * ── POR QUE UM FIO PRÓPRIO E NÃO O `pulse` DO SHELL ───────────────────────
 *
 * O stream de tenant (`useQueueStream`) leva `pulse(notifications)`, mas só o
 * caso de uso `Notify` o dispara — o "pedir ajuda" do agente, a proposta de
 * conhecimento e o card que mudou escrevem no repositório sem pulsar, e "marcar
 * como lida" noutra aba nunca avisou. E é um aviso do tenant inteiro: um recado
 * para uma pessoa mandava as outras trinta reler um sino que não mudou. O
 * servidor agora tem `GET /v3/notifications/stream`, POR PESSOA, alimentado por
 * todo caminho de escrita; é ele que se ouve aqui.
 *
 * ── POR QUE `fetch` E NÃO `EventSource` ───────────────────────────────────
 *
 * O mesmo motivo do quadro e da inbox, escrito em `shared/http/sse.ts`:
 * `EventSource` não leva `Authorization`, e a rota é autenticada como as outras.
 * O token é AGUARDADO — `getAuthToken` é uma promessa desde que a sessão
 * aprendeu a esperar pelo restore — porque interpolado cru vai como
 * `Bearer [object Promise]` e é 401 em toda abertura.
 *
 * ── POR QUE INVALIDA E NÃO REMENDA O CACHE ────────────────────────────────
 *
 * Só viajam o tipo e o id. O dado tem dono — a query do sino — e um evento que
 * o carregasse seria uma segunda fonte de verdade a divergir da primeira. Reler
 * uma página é barato e está sempre certo.
 *
 * ── QUEDA, RAJADA E ABA ESCONDIDA ─────────────────────────────────────────
 *
 * Uma queda é normal (deploy, wi-fi, proxy) e volta com recuo exponencial até
 * 30s — em laço quente, não. No REGRESSO relê-se uma vez, porque tudo o que
 * aconteceu com o fio caído não mandou evento nenhum. Cinco avisos do mesmo
 * instante (uma equipe num `unnest`) são UMA releitura, pela janela curta. E uma
 * aba escondida há mais de um minuto larga a ligação: um painel esquecido num
 * separador o dia inteiro não deve segurar um socket por um sino que ninguém
 * vê — ao voltar, reabre e o regresso reconcilia.
 */
export const NOTIFICATION_STREAM_PATH = "/v3/notifications/stream";
/** O recuo entre tentativas, uma queda após outra. O último repete-se. */
export const BACKOFF_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
/** Quanto tempo uma aba fica escondida antes de largar o fio. */
export const HIDDEN_GRACE_MS = 60_000;
/** A janela que junta uma rajada de avisos numa releitura só. */
const COALESCE_MS = 50;
/** Os tipos que o servidor emite; um heartbeat nunca chega aqui, um tipo novo é ignorado. */
const KINDS = new Set(["created", "read", "dismissed"]);

type Timer = ReturnType<typeof setTimeout>;

/**
 * Abre o fio e devolve a função que o fecha.
 *
 * Separado do composable para ser testável sem componente: o que aqui interessa —
 * invalidar ao ouvir, voltar depois da queda, largar a aba escondida — não tem
 * nada de Vue.
 */
export function watchNotifications(client: QueryClient, doc: Document = document): () => void {
	let controller: AbortController | null = null;
	let attempts = 0;
	let reconnect: Timer | null = null;
	let hiding: Timer | null = null;
	let coalescing: Timer | null = null;
	let stopped = false;
	/** Escondida há tempo demais: o fio foi largado de propósito e não deve voltar sozinho. */
	let parked = false;
	/** Se alguma ligação já vingou: o regresso de uma queda relê, a primeira abertura não. */
	let hadConnection = false;

	function invalidateSoon(): void {
		if (coalescing) return;
		coalescing = setTimeout(() => {
			coalescing = null;
			void client.invalidateQueries({ queryKey: notificationKeys.all() });
		}, COALESCE_MS);
	}

	function scheduleReconnect(): void {
		if (stopped || parked || reconnect) return;
		const delay = BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)] ?? 30_000;
		attempts += 1;
		reconnect = setTimeout(() => {
			reconnect = null;
			connect();
		}, delay);
	}

	async function open(signal: AbortSignal): Promise<void> {
		try {
			const token = await getAuthToken();
			// Sem sessão não há a que assinar — e não se insiste: a preferência de
			// quem saiu não pode virar um `Bearer` vazio reaberto para sempre.
			if (!token || signal.aborted) return;

			const response = await fetch(NOTIFICATION_STREAM_PATH, {
				headers: { Authorization: `Bearer ${token}` },
				signal,
			});
			if (!response.ok || !response.body) throw new Error(`stream ${response.status}`);

			attempts = 0;
			// A leitura que fecha o buraco: o que aconteceu com o fio caído não avisou.
			if (hadConnection) invalidateSoon();
			hadConnection = true;

			await readEventStream(
				response.body,
				(event) => {
					if (KINDS.has(event.event)) invalidateSoon();
				},
				signal,
			);
		} catch {
			// Uma ligação caída não mostra nada e não parte nada: o refetch no foco
			// continua a existir por baixo. O recuo abaixo é o que a traz de volta.
		} finally {
			// O fim normal (o servidor fechou) e a queda saem pelo mesmo sítio: ambos
			// pedem uma nova ligação, a não ser que fomos nós a fechar.
			if (!signal.aborted) scheduleReconnect();
		}
	}

	function connect(): void {
		controller?.abort();
		controller = new AbortController();
		void open(controller.signal);
	}

	function drop(): void {
		if (reconnect) clearTimeout(reconnect);
		reconnect = null;
		controller?.abort();
		controller = null;
	}

	function onVisibility(): void {
		if (doc.visibilityState === "hidden") {
			// Um minuto de tolerância: mudar de aba por dez segundos não deve custar
			// uma reconexão e uma releitura ao voltar.
			if (!hiding) hiding = setTimeout(park, HIDDEN_GRACE_MS);
			return;
		}
		if (hiding) clearTimeout(hiding);
		hiding = null;
		if (!parked) return;
		parked = false;
		attempts = 0;
		connect();
	}

	function park(): void {
		hiding = null;
		parked = true;
		drop();
	}

	doc.addEventListener("visibilitychange", onVisibility);
	connect();

	return () => {
		stopped = true;
		doc.removeEventListener("visibilitychange", onVisibility);
		if (hiding) clearTimeout(hiding);
		if (coalescing) clearTimeout(coalescing);
		drop();
	};
}

/** O fio, enquanto o sino estiver montado — o cabeçalho, que só existe com sessão. */
export function useNotificationStream(): void {
	const client = useQueryClient();
	let stop: (() => void) | null = null;
	onMounted(() => {
		stop = watchNotifications(client);
	});
	onBeforeUnmount(() => {
		stop?.();
		stop = null;
	});
}
